import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu as MenuIcon, Calendar as CalendarIcon, Home as HomeIcon, MessagesSquare } from 'lucide-react';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import NotificationBell from './NotificationBell';
import useIsPhone from '../../hooks/useIsPhone';
import { DiscussionsProvider, useDiscussions } from '../../context/DiscussionsContext';

// PlanningSidebar est gros (~2000 lignes + RdvForm). On le lazy-load pour
// ne pas l'inclure dans le bundle initial — il n'est visible qu'après clic
// sur mobile, et sur xl+ une fois que tout le reste est rendu.
const PlanningSidebar = lazy(() => import('./PlanningSidebar'));

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [planningOpen, setPlanningOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isPhone = useIsPhone();

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close mobile sidebar on resize to desktop (only when not detected as phone)
  useEffect(() => {
    if (isPhone) return;
    const onResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false);
      // Le planning ne se ferme plus au redimensionnement : c'est un panneau
      // à toutes les tailles depuis que l'agenda est passé dans le menu.
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isPhone]);

  // Si on est sur un téléphone, on force le layout mobile peu importe le viewport
  // (corrige le mode "Site pour ordinateur" de Chrome/Samsung qui ment sur la largeur).
  // Sinon on laisse Tailwind gérer via les breakpoints lg:/xl:.
  const sidebarWrapperCls = isPhone
    ? `fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`
    : `fixed inset-y-0 left-0 z-50 lg:static lg:z-auto transform transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`;

  const overlayCls = isPhone ? 'fixed inset-0 bg-black/60 z-40' : 'fixed inset-0 bg-black/60 z-40 lg:hidden';
  const planningOverlayCls = isPhone ? 'fixed inset-0 bg-black/60 z-40' : 'fixed inset-0 bg-black/60 z-40 ';

  // Le planning est désormais un panneau, à toutes les tailles d'écran :
  // l'agenda vit dans le tiers bas du menu de gauche, et « Voir tout l'agenda »
  // ouvre ceci par-dessus. Avant, il occupait en permanence une colonne à
  // droite sur xl+, ce qui rétrécissait le contenu de chaque page.
  const planningWrapperCls =
    `fixed inset-y-0 right-0 z-50 transform transition-transform duration-300 ease-in-out ${planningOpen ? 'translate-x-0' : 'translate-x-full'}`;

  const mainCls = isPhone
    ? 'flex-1 overflow-auto min-w-0 pb-[calc(56px+env(safe-area-inset-bottom))]'
    : 'flex-1 overflow-auto min-w-0 pb-[calc(56px+env(safe-area-inset-bottom))] lg:pb-0';

  const bottomNavCls = isPhone
    ? 'fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur border-t border-slate-700 grid grid-cols-5'
    : 'fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur border-t border-slate-700 lg:hidden grid grid-cols-5';

  return (
    <DiscussionsProvider>
    <div className="flex h-screen bg-slate-900 overflow-hidden">
      {/* Overlay drawer menu */}
      {mobileOpen && (
        <div className={overlayCls} onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar — drawer sur mobile, statique sur desktop */}
      <div className={sidebarWrapperCls} style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          onClose={() => setMobileOpen(false)}
          isMobile={mobileOpen}
          onOpenPlanning={() => setPlanningOpen(true)}
        />
      </div>

      {/* Main content — barre du haut fixe + contenu défilant en dessous */}
      <div className="flex-1 flex flex-col min-w-0" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <TopBar onOpenMenu={() => setMobileOpen(true)} />
        <main className={mainCls}>
          <div className="p-3 sm:p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Barre de navigation mobile fixée en bas (zone pouce) */}
      <nav className={bottomNavCls} style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <button onClick={() => setMobileOpen(true)}
          className="flex flex-col items-center justify-center gap-0.5 py-2 text-slate-400 active:bg-slate-800"
          aria-label="Menu">
          <MenuIcon size={22} />
          <span className="text-[10px]">Menu</span>
        </button>
        <NotificationBell variant="mobile" />
        <BoutonDiscussionsMobile />
        <button onClick={() => navigate('/')}
          className="flex flex-col items-center justify-center gap-0.5 py-2 text-slate-400 active:bg-slate-800"
          aria-label="Accueil">
          <HomeIcon size={22} />
          <span className="text-[10px]">Accueil</span>
        </button>
        <button onClick={() => setPlanningOpen(true)}
          className={`flex flex-col items-center justify-center gap-0.5 py-2 text-slate-400 active:bg-slate-800 `}
          aria-label="Planning">
          <CalendarIcon size={22} />
          <span className="text-[10px]">Planning</span>
        </button>
      </nav>

      {/* Planning drawer — wrapper toujours monté pour l'animation, contenu lazy */}
      {planningOpen && (
        <div className={planningOverlayCls} onClick={() => setPlanningOpen(false)} />
      )}
      <div className={planningWrapperCls} style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        {/* Monté seulement une fois ouvert : le chunk PlanningSidebar (~1000
            lignes + RdvForm) ne pèse plus sur le chargement de chaque page. */}
        {planningOpen && (
          <Suspense fallback={<div className="w-screen sm:w-64 xl:w-56 h-full bg-slate-900" />}>
            <PlanningSidebar onClose={() => setPlanningOpen(false)} />
          </Suspense>
        )}
      </div>
    </div>
    </DiscussionsProvider>
  );
}

// Bouton Discussions de la barre basse, avec badge non-lus.
// Doit vivre sous DiscussionsProvider, d'ou un composant separe.
function BoutonDiscussionsMobile() {
  const { unreadTotal } = useDiscussions();
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate('/discussions')}
      className="relative flex flex-col items-center justify-center gap-0.5 py-2 text-slate-400 active:bg-slate-800"
      aria-label="Discussions"
    >
      <MessagesSquare size={22} />
      {unreadTotal > 0 && (
        <span className="absolute top-1 left-1/2 ml-1 min-w-[16px] h-4 px-1 rounded-full bg-purple-500 text-white text-[10px] flex items-center justify-center">
          {unreadTotal > 99 ? '99+' : unreadTotal}
        </span>
      )}
      <span className="text-[10px]">Chat</span>
    </button>
  );
}
